import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const gen = useRef(0);

  const check = useCallback(async () => {
    const my = ++gen.current;
    const apply = (u) => { if (my === gen.current) setUser(u); };
    try {
      const { data } = await api.get("/auth/me");
      apply(data);
    } catch {
      try {
        await api.post("/auth/refresh");
        const { data } = await api.get("/auth/me");
        apply(data);
      } catch {
        apply(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!window.location.hash.includes("session_id=")) check();
    const onExpired = () => { gen.current += 1; setUser(false); };
    window.addEventListener("auth:expired", onExpired);
    return () => window.removeEventListener("auth:expired", onExpired);
  }, [check]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    gen.current += 1;
    setUser(data);
  }, []);
  const register = useCallback(async (form) => {
    const { data } = await api.post("/auth/register", form);
    gen.current += 1;
    setUser(data);
  }, []);
  const googleLogin = useCallback(async (sessionId) => {
    gen.current += 1;
    try {
      const { data } = await api.post("/auth/google", { session_id: sessionId });
      setUser(data);
    } catch (e) {
      setUser(false);
      throw e;
    }
  }, []);
  const logout = useCallback(async () => {
    await api.post("/auth/logout");
    gen.current += 1;
    setUser(false);
  }, []);

  const value = useMemo(() => ({ user, login, register, googleLogin, logout }), [user, login, register, googleLogin, logout]);

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
